/**
 * Hook for V3 RFP Structure Extraction
 * Manages extraction state, polling, and caching
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  extractOpportunityStructureV3,
  getOpportunityStructureV3,
  getOpportunityExtractionStatusV3,
} from '../api/opportunities';
import type { RfpStructureMetadata, RfpExtractionStatus } from '../types/rfp';

export interface V3ExtractionState {
  status: RfpExtractionStatus;
  structure: RfpStructureMetadata | null;
  qualityScore: number | null;
  extractedAt: string | null;
  errorMessage: string | null;
  isLoading: boolean;
  isExtracting: boolean;
  canReExtract: boolean;
  extractionMethod: string | null;
}

export interface V3ExtractionActions {
  triggerExtraction: (artifactIds?: string[], forceReExtract?: boolean) => Promise<void>;
  fetchStructure: () => Promise<void>;
  checkStatus: () => Promise<void>;
  reset: () => void;
}

export interface UseRFPV3ExtractionOptions {
  opportunityId: string;
  projectId: string;
  autoFetch?: boolean; // Automatically fetch on mount
  pollInterval?: number; // Poll interval in ms when extracting (default: 2000)
}

export function useRFPV3Extraction({
  opportunityId,
  projectId,
  autoFetch = true,
  pollInterval = 2000,
}: UseRFPV3ExtractionOptions): [V3ExtractionState, V3ExtractionActions] {
  const [state, setState] = useState<V3ExtractionState>({
    status: 'pending',
    structure: null,
    qualityScore: null,
    extractedAt: null,
    errorMessage: null,
    isLoading: false,
    isExtracting: false,
    canReExtract: true,
    extractionMethod: null,
  });

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  /**
   * Check extraction status
   */
  const checkStatus = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      const statusData = await getOpportunityExtractionStatusV3(opportunityId, projectId);

      if (!isMountedRef.current) return;

      // Check if statusData is valid
      if (!statusData || typeof statusData !== 'object') {
        console.warn('[V3 Extraction] Invalid response from extraction status API:', statusData);
        throw new Error('Invalid response from extraction status API');
      }

      setState((prev) => ({
        ...prev,
        status: statusData.extraction_status || 'pending',
        qualityScore: statusData.quality_score ?? null,
        extractedAt: statusData.extracted_at ?? null,
        errorMessage: statusData.error_message ?? null,
        canReExtract: statusData.can_re_extract ?? true,
        extractionMethod: statusData.extraction_method ?? null,
        isLoading: false,
        isExtracting: statusData.extraction_status === 'extracting',
      }));

      // If extraction is complete, fetch the full structure
      if (statusData.extraction_status === 'completed') {
        await fetchStructure();
      }
    } catch (error: any) {
      console.error('[V3 Extraction] Error checking status:', error);
      if (!isMountedRef.current) return;

      setState((prev) => ({
        ...prev,
        isLoading: false,
        isExtracting: false,
        errorMessage: error.message || 'Failed to check extraction status',
      }));
    }
  }, [opportunityId, projectId]);

  /**
   * Fetch extracted structure
   */
  const fetchStructure = useCallback(async () => {
    if (!isMountedRef.current) return;

    setState((prev) => ({ ...prev, isLoading: true }));

    try {
      const structureData = await getOpportunityStructureV3(opportunityId, projectId);

      if (!isMountedRef.current) return;

      // Check if structureData is valid
      if (!structureData || typeof structureData !== 'object') {
        console.warn('[V3 Extraction] Invalid response from structure API:', structureData);
        throw new Error('Invalid response from structure API');
      }

      setState((prev) => ({
        ...prev,
        structure: structureData.structure ?? null,
        status: structureData.extraction_status || 'pending',
        qualityScore: structureData.quality_score ?? null,
        extractedAt: structureData.extracted_at ?? null,
        isLoading: false,
        isExtracting: false,
      }));
    } catch (error: any) {
      console.error('[V3 Extraction] Error fetching structure:', error);
      if (!isMountedRef.current) return;

      setState((prev) => ({
        ...prev,
        isLoading: false,
        isExtracting: false,
        errorMessage: error.message || 'Failed to fetch structure',
      }));
    }
  }, [opportunityId, projectId]);

  /**
   * Trigger V3 extraction
   */
  const triggerExtraction = useCallback(
    async (artifactIds?: string[], forceReExtract: boolean = false) => {
      if (!isMountedRef.current) return;

      setState((prev) => ({ ...prev, isLoading: true, isExtracting: true, errorMessage: null }));

      try {
        const extractionResult = await extractOpportunityStructureV3(
          opportunityId,
          projectId,
          artifactIds,
          forceReExtract
        );

        console.log('[V3 Extraction] Extraction triggered:', extractionResult);

        if (!isMountedRef.current) return;

        // Check if extractionResult is valid
        if (!extractionResult || typeof extractionResult !== 'object') {
          console.warn('[V3 Extraction] Invalid response from extraction API:', extractionResult);
          throw new Error('Invalid response from extraction API');
        }

        setState((prev) => ({
          ...prev,
          status: 'extracting',
          isExtracting: true,
          isLoading: false,
          extractionMethod: extractionResult.extraction_method ?? null,
        }));

        // Start polling for status updates
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }

        pollIntervalRef.current = setInterval(async () => {
          await checkStatus();
        }, pollInterval);
      } catch (error: any) {
        console.error('[V3 Extraction] Error triggering extraction:', error);
        if (!isMountedRef.current) return;

        setState((prev) => ({
          ...prev,
          isLoading: false,
          isExtracting: false,
          status: 'failed',
          errorMessage: error.message || 'Failed to trigger extraction',
        }));
      }
    },
    [opportunityId, projectId, pollInterval, checkStatus]
  );

  /**
   * Reset state
   */
  const reset = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    setState({
      status: 'pending',
      structure: null,
      qualityScore: null,
      extractedAt: null,
      errorMessage: null,
      isLoading: false,
      isExtracting: false,
      canReExtract: true,
      extractionMethod: null,
    });
  }, []);

  /**
   * Stop polling when extraction is complete or failed
   */
  useEffect(() => {
    if (state.status === 'completed' || state.status === 'failed') {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [state.status]);

  /**
   * Auto-fetch on mount if enabled
   */
  useEffect(() => {
    if (autoFetch && opportunityId && projectId) {
      checkStatus();
    }
  }, [autoFetch, opportunityId, projectId, checkStatus]);

  return [
    state,
    {
      triggerExtraction,
      fetchStructure,
      checkStatus,
      reset,
    },
  ];
}
